import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {NavigationService} from '../../../core/services/navigation/navigation.service';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {finalize, timer} from 'rxjs';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {ConfigService} from '../../../core/services/config/config.service';

@Component({
  selector: 'xp-two-factor-check',
  templateUrl: './two-factor-check.component.html',
  styleUrls: ['../authentication.common.scss', './two-factor-check.component.scss'],
  standalone: false,
})
export class TwoFactorCheckComponent implements OnInit {

  public readonly formGroup = this._formBuilder.group({
    otp: ['', [Validators.required.bind(Validators), Validators.pattern('[0-9]{6}')]],
  });

  private _canGenerateNewCode: boolean = false;

  private _isProcessing: boolean = false;
  private _errorKey: string = '';
  private _informationKey: string = '';

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _authenticationService: AuthenticationService,
    private readonly _navigationService: NavigationService,
    private readonly _configService: ConfigService,
  ) {
  }

  public get isProcessing(): boolean {
    return this._isProcessing;
  }

  public get errorKey(): string {
    return this._errorKey;
  }

  public get informationKey(): string {
    return this._informationKey;
  }

  public get otpEmpty(): boolean {
    return ((this.formGroup.controls['otp'].value as string) === '');
  }

  public get canGenerateNewCode(): boolean {
    return this._canGenerateNewCode;
  }

  public ngOnInit(): void {
    this.disableAskNewCode();
  }

  public clearOtp(): void {
    this.formGroup.controls['otp'].setValue('');
  }

  public submit2FA(): void {
    if (this.formGroup.invalid) {
      return;
    }
    this._errorKey = '';
    this._isProcessing = true;
    this._authenticationService.checkDualFactorCode(
      this.formGroup.controls['otp'].value as string,
    ).pipe(
      finalize(() => {
        this._isProcessing = false;
        this.formGroup.reset();
      }),
    ).subscribe({
      next: async () => {
        await this._navigationService.navigateTo(this._navigationService.defaultRoute);
      },
      error: (error: any) => {
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized as number) {
          this._errorKey = 'AUTHENTICATION.TWO_FACTOR_CHECK.INVALID_2FA_CODE';
        } else {
          this._errorKey = 'AUTHENTICATION.TWO_FACTOR_CHECK.UNKNOWN_ERROR';
        }
      },
    });
  }

  public resend2FACode(): void {
    this._informationKey = '';
    this._errorKey = '';
    this._authenticationService.resendDualFactorCode().subscribe({
      next: () => {
        this._informationKey = 'AUTHENTICATION.TWO_FACTOR_CHECK.2FA_CODE_RESENT';
      },
      error: async (error: any) => {
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized as number) {
          await this._navigationService.navigateTo(RoutesEnum.AUTHENTICATION);
        } else {
          this._errorKey = 'AUTHENTICATION.TWO_FACTOR_CHECK.UNKNOWN_ERROR';
        }
      },
    });
    this.disableAskNewCode();
  }

  private disableAskNewCode(): void {
    this._canGenerateNewCode = false;
    const timerInMs = this._configService.config.dualFactorAuthRegenerateIntervalInSeconds * 1000;
    timer(timerInMs).subscribe(() => {
      this._canGenerateNewCode = true;
      this._informationKey = '';
    });
  }

}
